import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { initializeApi } from './services/api'
import { useAuthStore } from './store/authStore'
import { ToastProvider } from './components/Toast'

// Auth pages
import LoginPage from './pages/auth/LoginPage'

// Admin pages
import AdminLayout from './layouts/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import EmployeesPage from './pages/admin/EmployeesPage'
import AttendanceAdminPage from './pages/admin/AttendanceAdminPage'
import ReportsAdminPage from './pages/admin/ReportsAdminPage'
import CentersPage from './pages/admin/CentersPage'
import AuditLogPage from './pages/admin/AuditLogPage'
import AdminLeavePage from './pages/admin/AdminLeavePage'
import HolidaysPage from './pages/admin/HolidaysPage'

// Employee pages
import EmployeeLayout from './layouts/EmployeeLayout'
import EmployeeDashboard from './pages/employee/EmployeeDashboard'
import AttendanceHistoryPage from './pages/employee/AttendanceHistoryPage'
import EODReportPage from './pages/employee/EODReportPage'
import LeaveRequestPage from './pages/employee/LeaveRequestPage'
import ProfilePage from './pages/employee/ProfilePage'
import HolidayCalendarPage from './pages/employee/HolidayCalendarPage'

// Executive pages
import LandingPage from './pages/LandingPage'
import ExecutiveLoginPage from './pages/executive/ExecutiveLoginPage'
import ExecutiveDashboard from './pages/executive/ExecutiveDashboard'
import { useExecutiveAuthStore } from './store/executiveAuthStore'
import ExecutiveChangePasswordPage from './pages/executive/Executivechangepasswordpage'
import WeekOffConfigPage from './pages/admin/WeekOffConfigPage'

// Protected Route component
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && (!user || !allowedRoles.includes(user.role))) {
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}

function ExecutiveRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useExecutiveAuthStore()
  if (!isAuthenticated) return <Navigate to="/executive/login" replace />
  return <>{children}</>
}

export default function App() {
  useEffect(() => {
    initializeApi()
  }, [])

  return (
    <ToastProvider>
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Admin routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin', 'viewer']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="attendance" element={<AttendanceAdminPage />} />
          <Route path="reports" element={<ReportsAdminPage />} />
          <Route path="centers" element={<CentersPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="leave" element={<AdminLeavePage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="holidays" element={<HolidaysPage />} />
          <Route path="week-offs" element={<WeekOffConfigPage />} />
          <Route path="" element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* Employee routes */}
        <Route
          path="/employee/*"
          element={
            <ProtectedRoute allowedRoles={['employee']}>
              <EmployeeLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<EmployeeDashboard />} />
          <Route path="history" element={<AttendanceHistoryPage />} />
          <Route path="eod-report" element={<EODReportPage />} />
          <Route path="leave" element={<LeaveRequestPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="holidays" element={<HolidayCalendarPage />} />
          <Route path="" element={<Navigate to="dashboard" replace />} />
          

        </Route>

       {/* Landing page — portal selection */}
        <Route path="/" element={<LandingPage />} />

        {/* Executive routes — completely separate auth */}
        <Route path="/executive/login" element={<ExecutiveLoginPage />} />
        <Route
          path="/executive/dashboard"
          element={
            <ExecutiveRoute>
              <ExecutiveDashboard />
            </ExecutiveRoute>
          }
        />
        <Route
          path="/executive/change-password"
          element={
            <ExecutiveRoute>
              <ExecutiveChangePasswordPage />
            </ExecutiveRoute>
          }
        />

        {/* Root redirect */}
        {/* <Route
          path="/"
          element={
            useAuthStore.getState().isAuthenticated ? (
              <Navigate to={`/${useAuthStore.getState().user?.role}/dashboard`} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        /> */}

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
  )
}
